/**
 * Google Consent Mode v2 signalling (GitLab #649).
 *
 * Two separate things have to be true for us to be compliant, and they are
 * easy to confuse:
 *
 *   1. **Nothing loads without consent.** Handled by the gate — GTM is not
 *      injected at all until an optional category is granted.
 *   2. **Whatever does load is told what it may do.** Handled here. Even
 *      after a customer accepts analytics but refuses marketing, GTM is
 *      running, and Consent Mode is what stops the advertising tags storing
 *      anything.
 *
 * The defaults are emitted inline, before any other script, so a tag can
 * never observe an unset consent state — Google treats "unset" as its own
 * regional default, which in the UK would be the wrong answer.
 */

import type { ConsentCategories } from './consentCookie';

/** The Consent Mode v2 signals Google recognises. */
export interface ConsentModeSignals {
  ad_storage: 'granted' | 'denied';
  ad_user_data: 'granted' | 'denied';
  ad_personalization: 'granted' | 'denied';
  analytics_storage: 'granted' | 'denied';
  functionality_storage: 'granted' | 'denied';
  personalization_storage: 'granted' | 'denied';
  security_storage: 'granted' | 'denied';
}

/**
 * Everything denied except `security_storage`, which covers authentication
 * and fraud prevention and is strictly necessary — Google's own guidance is
 * that it stays granted, and denying it would break the very protections a
 * financially vulnerable customer relies on.
 */
export const DENIED_BY_DEFAULT_SIGNALS: ConsentModeSignals = {
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
  analytics_storage: 'denied',
  functionality_storage: 'denied',
  personalization_storage: 'denied',
  security_storage: 'granted',
};

const flag = (granted: boolean): 'granted' | 'denied' =>
  granted ? 'granted' : 'denied';

/** Translate our four customer-facing categories into Google's signals. */
export function toConsentModeSignals(
  categories: ConsentCategories,
): ConsentModeSignals {
  return {
    ad_storage: flag(categories.marketing),
    ad_user_data: flag(categories.marketing),
    ad_personalization: flag(categories.marketing),
    analytics_storage: flag(categories.analytics),
    functionality_storage: flag(categories.functional),
    personalization_storage: flag(categories.functional),
    security_storage: 'granted',
  };
}

/**
 * Source of the inline bootstrap script.
 *
 * Written as a plain string rather than a component body because it has to
 * run before React hydrates — by the time a client component could push to
 * `dataLayer`, a tag may already have read the unset state.
 *
 * `wait_for_update` gives the gate a 500ms window to apply a stored choice
 * before any tag acts on the defaults, which avoids a pageview being
 * recorded as unconsented purely because hydration was slow.
 *
 * No interpolation of any kind: the string is a constant, so there is
 * nothing for a hostile org name or container ID to escape into. It is
 * served under the request nonce like every other inline script.
 */
export const CONSENT_MODE_DEFAULT_SCRIPT = `
window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
gtag('consent', 'default', ${JSON.stringify({
  ...DENIED_BY_DEFAULT_SIGNALS,
  wait_for_update: 500,
})});
`.trim();

/** Minimal shape of the GTM data layer we rely on. */
type DataLayer = Array<Record<string, unknown> | IArguments>;

interface DataLayerWindow extends Window {
  dataLayer?: DataLayer;
}

/**
 * Return the data layer, creating it if the bootstrap script has not run.
 * Returns `null` off the browser so callers need no environment check.
 */
export function getDataLayer(): DataLayer | null {
  if (typeof window === 'undefined') return null;
  const w = window as DataLayerWindow;
  if (!w.dataLayer) w.dataLayer = [];
  return w.dataLayer;
}

/**
 * Push a consent update. Pushes to the data layer directly rather than
 * looking for a global `gtag` function, because the update has to work
 * whether or not GTM has loaded yet — queued entries are replayed when it
 * does.
 *
 * The entry must be a genuine `arguments` object: GTM only treats an entry
 * as a command when it is one, and stores anything else (including a plain
 * object with numeric keys) as ordinary data, silently dropping the update.
 */
export function pushConsentUpdate(categories: ConsentCategories): void {
  const dataLayer = getDataLayer();
  if (!dataLayer) return;
  const signals = toConsentModeSignals(categories);
  function gtag(..._args: unknown[]): void {
    // eslint-disable-next-line prefer-rest-params
    dataLayer!.push(arguments);
  }
  gtag('consent', 'update', signals);
}

/**
 * Announce the customer's choice as a `dataLayer` event so GTM triggers can
 * fire off it. Carries only the category booleans — never an identifier, an
 * email or anything from a workflow answer.
 */
export function pushConsentEvent(
  event: 'consent_granted' | 'consent_updated' | 'consent_withdrawn',
  categories: ConsentCategories,
): void {
  const dataLayer = getDataLayer();
  if (!dataLayer) return;
  dataLayer.push({
    event,
    consent_functional: categories.functional,
    consent_analytics: categories.analytics,
    consent_marketing: categories.marketing,
  });
}
